import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Loader2 } from "lucide-react";
import { toast } from "react-toastify";
import axiosClient from "../../../api/axiosClient";
import { getBillingUsageDashboard } from "../../../api/dashboardService";
import { formatMessageRate } from "../../../utils/currencyFormatters";

const CHARGE_LOGS_ENDPOINT = "/billing/charge-logs";
const WHATSAPP_NUMBERS_ENDPOINT = "/whatsappsettings/META_CLOUD/numbers";
const DEFAULT_TAKE = 100;
const USAGE_START_DATE = "2026-10-01";
const TAB_DASHBOARD = "dashboard";
const TAB_REPORT = "report";

const CATEGORY_OPTIONS = [
  { value: "", label: "All categories" },
  { value: "marketing", label: "Marketing" },
  { value: "utility", label: "Utility" },
  { value: "authentication", label: "Authentication" },
  { value: "service", label: "Service" },
];

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "Charged", label: "Charged" },
  { value: "Free", label: "Free" },
  { value: "Updating", label: "Updating" },
  { value: "Not charged", label: "Not charged" },
];

const CHARGE_SERIES_COLORS = [
  "#0f766e",
  "#0ea5e9",
  "#f59e0b",
  "#16a34a",
  "#dc2626",
  "#475569",
];

function getEmptyDashboard() {
  return {
    summary: {
      sentMessages: 0,
      deliveredMessages: 0,
      receivedMessages: 0,
      awaitingBillingDetails: 0,
      paidMessages: 0,
      freeMessages: 0,
      totalCharges: [],
      averageCostPerPaidMessage: [],
    },
    categories: [],
    trends: [],
    insights: {
      highestVolumeCategory: null,
      highestCostCategories: [],
      freeToPaidRatio: null,
      chargeChanges: [],
    },
    limitations: [],
  };
}

function getDefaultDateRange() {
  const today = new Date();
  const fromDate = new Date(today);
  fromDate.setDate(today.getDate() - 29);

  const toInputValue = value => {
    const year = value.getFullYear();
    const month = `${value.getMonth() + 1}`.padStart(2, "0");
    const day = `${value.getDate()}`.padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  return {
    from: toInputValue(fromDate) < USAGE_START_DATE
      ? USAGE_START_DATE
      : toInputValue(fromDate),
    to: toInputValue(today),
  };
}

function extractEnvelopeArray(response) {
  const payload = response?.data?.payload;
  if (Array.isArray(payload)) return payload;

  const data = response?.data?.data;
  if (Array.isArray(data)) return data;

  return [];
}

function extractEnvelopeObject(response) {
  const payload = response?.data?.payload;
  if (payload && typeof payload === "object" && !Array.isArray(payload)) {
    return payload;
  }

  const data = response?.data?.data;
  if (data && typeof data === "object" && !Array.isArray(data)) {
    return data;
  }

  if (
    response?.data &&
    typeof response.data === "object" &&
    !Array.isArray(response.data)
  ) {
    return response.data;
  }

  return {};
}

function getErrorMessage(error, fallbackMessage) {
  const responseData = error?.response?.data;
  if (typeof responseData === "string" && responseData.trim()) {
    return responseData;
  }
  return (
    error?.response?.data?.message ||
    error?.response?.data?.errorMessage ||
    error?.message ||
    fallbackMessage
  );
}

function normalizeWhatsAppNumbers(response) {
  const items = Array.isArray(response?.data) ? response.data : extractEnvelopeArray(response);
  return items
    .map(item => ({
      id: String(item?.phoneNumberId ?? item?.PhoneNumberId ?? "").trim(),
      label: String(
        item?.senderDisplayName ?? item?.SenderDisplayName ??
        item?.whatsAppBusinessNumber ?? item?.WhatsAppBusinessNumber ??
        item?.phoneNumberId ?? item?.PhoneNumberId ?? "",
      ).trim(),
      isDefault: Boolean(item?.isDefault ?? item?.IsDefault),
      isActive: item?.isActive ?? item?.IsActive ?? true,
    }))
    .filter(item => item.id && item.isActive);
}

function toNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function toNullableNumber(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeMoneyRows(items) {
  if (!Array.isArray(items)) return [];

  return items
    .map(item => ({
      currency: String(item?.currency ?? item?.Currency ?? "")
        .trim()
        .toUpperCase(),
      amount: toNumber(item?.amount ?? item?.Amount),
    }))
    .filter(item => item.currency);
}

function normalizeDashboardPayload(payload) {
  const raw = payload || {};
  const summaryRaw = raw.summary ?? raw.Summary ?? {};
  const insightsRaw = raw.insights ?? raw.Insights ?? {};
  const categoriesRaw = raw.categories ?? raw.Categories ?? [];
  const trendsRaw = raw.trends ?? raw.Trends ?? [];
  const limitationsRaw = raw.limitations ?? raw.Limitations ?? [];

  return {
    summary: {
      sentMessages: toNumber(
        summaryRaw.sentMessages ?? summaryRaw.SentMessages,
      ),
      deliveredMessages: toNumber(
        summaryRaw.deliveredMessages ?? summaryRaw.DeliveredMessages,
      ),
      receivedMessages: toNumber(
        summaryRaw.receivedMessages ?? summaryRaw.ReceivedMessages,
      ),
      awaitingBillingDetails: toNumber(summaryRaw.awaitingBillingDetails ?? summaryRaw.AwaitingBillingDetails),
      paidMessages: toNumber(
        summaryRaw.paidMessages ??
          summaryRaw.PaidMessages,
      ),
      freeMessages: toNumber(
        summaryRaw.freeMessages ??
          summaryRaw.FreeMessages,
      ),
      totalCharges: normalizeMoneyRows(
        summaryRaw.totalCharges ?? summaryRaw.TotalCharges,
      ),
      averageCostPerPaidMessage: normalizeMoneyRows(
        summaryRaw.averageCostPerPaidMessage ??
          summaryRaw.AverageCostPerPaidMessage,
      ),
    },
    categories: Array.isArray(categoriesRaw)
      ? categoriesRaw.map(item => ({
          category:
            String(item?.category ?? item?.Category ?? "unknown").trim() ||
            "unknown",
          deliveredMessages: toNumber(
            item?.deliveredMessages ?? item?.DeliveredMessages,
          ),
          paidMessages: toNumber(
            item?.paidMessages ?? item?.PaidMessages,
          ),
          freeMessages: toNumber(
            item?.freeMessages ?? item?.FreeMessages,
          ),
          awaitingBillingDetails: toNumber(
            item?.awaitingBillingDetails ?? item?.AwaitingBillingDetails,
          ),
          charges: normalizeMoneyRows(item?.charges ?? item?.Charges),
        }))
      : [],
    trends: Array.isArray(trendsRaw)
      ? trendsRaw.map(item => ({
          date: String(item?.date ?? item?.Date ?? ""),
          sentMessages: toNumber(item?.sentMessages ?? item?.SentMessages),
          paidMessages: toNumber(
            item?.paidMessages ?? item?.PaidMessages,
          ),
          freeMessages: toNumber(
            item?.freeMessages ?? item?.FreeMessages,
          ),
          awaitingBillingDetails: toNumber(
            item?.awaitingBillingDetails ?? item?.AwaitingBillingDetails,
          ),
          charges: normalizeMoneyRows(item?.charges ?? item?.Charges),
        }))
      : [],
    insights: {
      highestVolumeCategory:
        String(
          insightsRaw.highestVolumeCategory ??
            insightsRaw.HighestVolumeCategory ??
            "",
        ).trim() || null,
      highestCostCategories: Array.isArray(
        insightsRaw.highestCostCategories ?? insightsRaw.HighestCostCategories,
      )
        ? (
            insightsRaw.highestCostCategories ??
            insightsRaw.HighestCostCategories
          ).map(item => ({
            currency: String(item?.currency ?? item?.Currency ?? "")
              .trim()
              .toUpperCase(),
            category:
              String(item?.category ?? item?.Category ?? "unknown").trim() ||
              "unknown",
            amount: toNumber(item?.amount ?? item?.Amount),
          }))
        : [],
      freeToPaidRatio: toNullableNumber(
        insightsRaw.freeToPaidRatio ?? insightsRaw.FreeToPaidRatio,
      ),
      chargeChanges: Array.isArray(
        insightsRaw.chargeChanges ?? insightsRaw.ChargeChanges,
      )
        ? (insightsRaw.chargeChanges ?? insightsRaw.ChargeChanges).map(item => ({
            currency: String(item?.currency ?? item?.Currency ?? "")
              .trim()
              .toUpperCase(),
            currentAmount: toNumber(
              item?.currentAmount ?? item?.CurrentAmount,
            ),
            previousAmount: toNumber(
              item?.previousAmount ?? item?.PreviousAmount,
            ),
            deltaAmount: toNumber(item?.deltaAmount ?? item?.DeltaAmount),
            deltaPercentage: toNullableNumber(
              item?.deltaPercentage ?? item?.DeltaPercentage,
            ),
          }))
        : [],
    },
    limitations: Array.isArray(limitationsRaw)
      ? limitationsRaw.filter(item => typeof item === "string" && item.trim())
      : [],
  };
}

function formatDateTime(value) {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function formatTrendDateLabel(value) {
  if (!value) return "-";

  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
  });
}

function formatMoney(amount, currency) {
  if (amount === null || amount === undefined) return "-";

  const numericAmount = Number(amount);
  if (!Number.isFinite(numericAmount)) return "-";

  const normalizedCurrency =
    String(currency || "INR").trim().toUpperCase() || "INR";

  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: normalizedCurrency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numericAmount);
}

function formatCompactNumber(value) {
  return Number(value || 0).toLocaleString("en-IN");
}

function csvEscape(value) {
  const text = value === null || value === undefined ? "" : String(value);
  return `"${text.replace(/"/g, '""')}"`;
}

function formatCategory(value) {
  if (!value) return "Unknown";

  const normalized = String(value).trim();
  if (!normalized) return "Unknown";

  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

function formatStatus(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) return "Unknown";

  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

function getStatusBadgeClass(status) {
  const normalized = String(status || "").trim().toLowerCase();

  if (normalized === "charged" || normalized === "free") {
    return "border-emerald-200 bg-emerald-50 text-emerald-700";
  }

  if (normalized === "updating") {
    return "border-amber-200 bg-amber-50 text-amber-700";
  }

  return "border-slate-200 bg-slate-50 text-slate-700";
}

function formatUsageAmount(value, currency) {
  if (!currency) return "-";
  return formatMessageRate(value, currency, {
    withSuffix: false,
    maximumFractionDigits: 6,
  });
}

function buildChargeTrendData(trends) {
  const currencies = [];
  const seen = new Set();

  trends.forEach(point => {
    (point.charges || []).forEach(entry => {
      if (entry.currency && !seen.has(entry.currency)) {
        seen.add(entry.currency);
        currencies.push(entry.currency);
      }
    });
  });

  const rows = trends.map(point => {
    const row = {
      label: formatTrendDateLabel(point.date),
      rawDate: point.date,
    };

    currencies.forEach(currency => {
      row[`charge_${currency}`] = 0;
    });

    (point.charges || []).forEach(entry => {
      row[`charge_${entry.currency}`] = toNumber(entry.amount);
    });

    return row;
  });

  return { currencies, rows };
}

function rowMatchesSearch(row, query) {
  const normalized = String(query || "").trim().toLowerCase();
  if (!normalized) return true;

  return [
    row?.occurredAtUtc,
    row?.category,
    row?.countryCode,
    row?.yourRate,
    row?.charge,
    row?.currency,
    row?.status,
  ]
    .map(value => String(value ?? "").toLowerCase())
    .some(value => value.includes(normalized));
}

function buildReportFilterChips(filters, search) {
  const chips = [];

  if (search.trim()) {
    chips.push({ key: "search", label: `Search: ${search.trim()}` });
  }

  if (filters.category) {
    chips.push({
      key: "category",
      label: `Category: ${formatCategory(filters.category)}`,
    });
  }

  if (filters.status) {
    chips.push({
      key: "status",
      label: `Status: ${formatStatus(filters.status)}`,
    });
  }

  return chips;
}

function MoneyStack({
  items,
  emptyLabel = "Not available",
  align = "left",
  dense = false,
}) {
  if (!Array.isArray(items) || items.length === 0) {
    return <span className="text-sm text-slate-500">{emptyLabel}</span>;
  }

  const alignmentClass =
    align === "right" ? "items-end text-right" : "items-start text-left";
  const gapClass = dense ? "gap-1" : "gap-1.5";

  return (
    <div className={`flex flex-col ${alignmentClass} ${gapClass}`}>
      {items.map(entry => (
        <div
          key={`${entry.currency}-${entry.amount}`}
          className="flex flex-wrap items-center gap-2"
        >
          <span className="text-sm font-semibold text-slate-900">
            {formatMoney(entry.amount, entry.currency)}
          </span>
          {items.length > 1 ? (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
              {entry.currency}
            </span>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function SectionCard({ title, subtitle, actions, children }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-col gap-3 border-b border-slate-100 px-4 py-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
          {subtitle ? (
            <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
          ) : null}
        </div>
        {actions ? <div className="flex flex-wrap gap-2">{actions}</div> : null}
      </div>
      <div className="px-4 py-4">{children}</div>
    </div>
  );
}

function TabButton({ active, id, panelId, children, onClick }) {
  return (
    <button
      type="button"
      role="tab"
      id={id}
      aria-selected={active}
      aria-controls={panelId}
      onClick={onClick}
      className={`border-b-2 pb-2 text-xs font-semibold tracking-normal transition ${
        active
          ? "border-emerald-600 text-emerald-700"
          : "border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-900"
      }`}
    >
      {children}
    </button>
  );
}

function ChartShell({ title, subtitle, loading, emptyMessage, children }) {
  return (
    <SectionCard title={title} subtitle={subtitle}>
      <div className="h-[280px]">
        {loading ? (
          <div className="flex h-full items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50 text-sm text-slate-500">
            <Loader2 className="mr-2 h-4 w-4 animate-spin text-emerald-600" />
            Loading chart...
          </div>
        ) : emptyMessage ? (
          <div className="flex h-full items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50 text-sm text-slate-500">
            {emptyMessage}
          </div>
        ) : (
          children
        )}
      </div>
    </SectionCard>
  );
}

function MetricTooltip({ active, payload, label, valueFormatter }) {
  if (!active || !Array.isArray(payload) || payload.length === 0) {
    return null;
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-lg">
      <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
        {label}
      </div>
      <div className="mt-2 space-y-1.5">
        {payload.map(item => (
          <div
            key={item.dataKey}
            className="flex items-center justify-between gap-4 text-sm"
          >
            <span className="text-slate-600">{item.name}</span>
            <span className="font-semibold text-slate-900">
              {valueFormatter
                ? valueFormatter(item.value, item)
                : formatCompactNumber(item.value)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function formatChargeTooltipValue(value, item) {
  const currency = String(item?.dataKey || "").replace("charge_", "");
  return formatMoney(value, currency);
}

function InsightCard({ title, helper, children }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
        Insight
      </div>
      <div className="mt-2 text-sm font-semibold text-slate-900">{title}</div>
      <div className="mt-3 min-h-[52px]">{children}</div>
      <div className="mt-3 text-sm text-slate-500">{helper}</div>
    </div>
  );
}

export default function UsageChargesPage() {
  const defaultDates = useMemo(() => getDefaultDateRange(), []);
  const [activeTab, setActiveTab] = useState(TAB_DASHBOARD);
  const [filters, setFilters] = useState({
    from: defaultDates.from,
    to: defaultDates.to,
    category: "",
    status: "",
  });
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [dashboardData, setDashboardData] = useState(() => getEmptyDashboard());
  const [isDashboardLoading, setIsDashboardLoading] = useState(true);
  const [dashboardError, setDashboardError] = useState("");
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [reportSearch, setReportSearch] = useState("");
  const [whatsappNumbers, setWhatsappNumbers] = useState([]);
  const [selectedPhoneNumberId, setSelectedPhoneNumberId] = useState("");
  const [allowanceData, setAllowanceData] = useState(null);
  const [allowanceLoading, setAllowanceLoading] = useState(false);
  const [allowanceError, setAllowanceError] = useState("");
  const [numbersLoading, setNumbersLoading] = useState(true);
  const allowanceRequestRef = useRef(0);
  const dashboardRequestRef = useRef(0);
  const chargeRequestRef = useRef(0);

  const loadWhatsAppNumbers = useCallback(async () => {
    try {
      setNumbersLoading(true);
      const response = await axiosClient.get(WHATSAPP_NUMBERS_ENDPOINT);
      const numbers = normalizeWhatsAppNumbers(response);
      setWhatsappNumbers(numbers);
      setSelectedPhoneNumberId(current =>
        numbers.some(number => number.id === current)
          ? current
          : numbers.find(number => number.isDefault)?.id || numbers[0]?.id || "",
      );
    } catch (error) {
      setWhatsappNumbers([]);
      setSelectedPhoneNumberId("");
      setAllowanceError(getErrorMessage(error, "Unable to load WhatsApp numbers."));
    } finally {
      setNumbersLoading(false);
    }
  }, []);

  const loadAllowance = useCallback(async phoneNumberId => {
    const requestId = ++allowanceRequestRef.current;
    if (!phoneNumberId) {
      setAllowanceData(null);
      setAllowanceLoading(false);
      return;
    }
    try {
      setAllowanceLoading(true);
      setAllowanceError("");
      const response = await axiosClient.get(
        `/billing/usage/numbers/${encodeURIComponent(phoneNumberId)}/allowance-reconciliation`,
        { __silentGenericToast: true },
      );
      if (requestId !== allowanceRequestRef.current) return;
      setAllowanceData(extractEnvelopeObject(response));
    } catch (error) {
      if (requestId !== allowanceRequestRef.current) return;
      setAllowanceData(null);
      if (error?.response?.status !== 404) {
        setAllowanceError("Allowance data could not be loaded.");
      }
    } finally {
      if (requestId === allowanceRequestRef.current) setAllowanceLoading(false);
    }
  }, []);

  useEffect(() => {
    loadWhatsAppNumbers();
  }, [loadWhatsAppNumbers]);

  useEffect(() => {
    loadAllowance(selectedPhoneNumberId);
    return () => { allowanceRequestRef.current += 1; };
  }, [loadAllowance, selectedPhoneNumberId]);

  const loadDashboard = useCallback(async nextFilters => {
    const requestId = ++dashboardRequestRef.current;
    setDashboardData(getEmptyDashboard());
    setDashboardError("");
    if (!selectedPhoneNumberId) {
      setIsDashboardLoading(false);
      return;
    }
    try {
      setIsDashboardLoading(true);
      setDashboardError("");

      const response = await getBillingUsageDashboard({
        from: nextFilters.from || undefined,
        to: nextFilters.to || undefined,
        phoneNumberId: selectedPhoneNumberId || undefined,
      });

      if (requestId !== dashboardRequestRef.current) return;
      setDashboardData(normalizeDashboardPayload(extractEnvelopeObject(response)));
    } catch (error) {
      if (requestId !== dashboardRequestRef.current) return;
      const message = getErrorMessage(
        error,
        "Failed to load usage dashboard.",
      );

      setDashboardError(message);
      setDashboardData(getEmptyDashboard());
      toast.error(message);
    } finally {
      if (requestId === dashboardRequestRef.current) setIsDashboardLoading(false);
    }
  }, [selectedPhoneNumberId]);

  const loadCharges = useCallback(async nextFilters => {
    const requestId = ++chargeRequestRef.current;
    setRows([]);
    setLoadError("");
    if (!selectedPhoneNumberId) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setLoadError("");

      const response = await axiosClient.get(CHARGE_LOGS_ENDPOINT, {
        params: {
          from: nextFilters.from || undefined,
          to: nextFilters.to || undefined,
          category: nextFilters.category || undefined,
          status: nextFilters.status || undefined,
          phoneNumberId: selectedPhoneNumberId,
          take: DEFAULT_TAKE,
        },
        __silentGenericToast: true,
      });

      if (requestId !== chargeRequestRef.current) return;
      const items = extractEnvelopeArray(response);
      setRows(Array.isArray(items) ? items : []);
    } catch (error) {
      if (requestId !== chargeRequestRef.current) return;
      const message = getErrorMessage(
        error,
        "Failed to load usage charges.",
      );

      setLoadError(message);
      setRows([]);
      toast.error(message);
    } finally {
      if (requestId === chargeRequestRef.current) setIsLoading(false);
    }
  }, [selectedPhoneNumberId]);

  useEffect(() => {
    if (numbersLoading) return;
    loadDashboard({ from: appliedFilters.from, to: appliedFilters.to });
    return () => { dashboardRequestRef.current += 1; };
  }, [numbersLoading, loadDashboard, appliedFilters.from, appliedFilters.to]);

  useEffect(() => {
    if (numbersLoading) return;
    loadCharges(appliedFilters);
    return () => { chargeRequestRef.current += 1; };
  }, [numbersLoading, loadCharges, appliedFilters]);

  const filteredRows = useMemo(() => {
    let nextRows = rows;

    if (reportSearch.trim()) {
      nextRows = nextRows.filter(row => rowMatchesSearch(row, reportSearch));
    }

    return nextRows;
  }, [reportSearch, rows]);

  const volumeTrendData = useMemo(
    () =>
      dashboardData.trends.map(point => ({
        label: formatTrendDateLabel(point.date),
        sentMessages: point.sentMessages,
        paidMessages: point.paidMessages,
        freeMessages: point.freeMessages,
        awaitingBillingDetails: point.awaitingBillingDetails,
      })),
    [dashboardData.trends],
  );

  const chargeTrend = useMemo(
    () => buildChargeTrendData(dashboardData.trends),
    [dashboardData.trends],
  );

  const reportFilterChips = useMemo(
    () => buildReportFilterChips(appliedFilters, reportSearch),
    [appliedFilters, reportSearch],
  );

  const handleFilterChange = key => event => {
    const value = event.target.value;
    setFilters(current => ({ ...current, [key]: value }));
  };

  const handleApplyRange = () => {
    setAppliedFilters({ ...filters });
  };

  const handleResetRange = () => {
    const resetFilters = {
      ...filters,
      from: defaultDates.from,
      to: defaultDates.to,
    };

    setFilters(resetFilters);
    setAppliedFilters(resetFilters);
  };

  const handleLoadReport = () => {
    setAppliedFilters({ ...filters });
  };

  const handleResetReport = () => {
    const resetFilters = {
      ...filters,
      category: "",
      status: "",
    };

    setFilters(resetFilters);
    setReportSearch("");
    setAppliedFilters(resetFilters);
  };

  const handleClearReportChip = key => {
    if (key === "search") {
      setReportSearch("");
      return;
    }

    if (key === "category" || key === "status") {
      setFilters(current => ({ ...current, [key]: "" }));
      setAppliedFilters(current => ({ ...current, [key]: "" }));
    }
  };

  const handleExportReport = () => {
    if (!filteredRows.length) return;

    const headers = [
      "Date / Time",
      "Category",
      "Country",
      "Status",
      "Your Rate",
      "Charge",
      "Currency",
    ];

    const lines = filteredRows.map(row =>
      [
        row?.occurredAtUtc || "",
        formatCategory(row?.category),
        row?.countryCode || "",
        row?.status || "",
        row?.yourRate ?? "",
        row?.charge ?? "",
        row?.currency || "",
      ]
        .map(csvEscape)
        .join(","),
    );

    const csv = [headers.map(csvEscape).join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `usage-charges-${appliedFilters.from || "from"}-${appliedFilters.to || "to"}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const summary = dashboardData.summary;
  const allowance = allowanceData || {};
  const allowanceField = (camel, pascal = camel) => allowance[camel] ?? allowance[pascal];
  const summaryMetrics = [
    { label: "Paid messages", value: summary.paidMessages },
    { label: "Free messages", value: summary.freeMessages },
    { label: "Charges updating", value: summary.awaitingBillingDetails },
  ];

  return (
    <div className="min-h-screen bg-slate-50 px-6 py-6">
      <div className="mx-auto max-w-7xl space-y-5">
        <header className="space-y-1">
          <h1 className="text-xl font-semibold tracking-tight text-slate-900">
            Usage & Charges
          </h1>
          <p className="max-w-3xl text-xs text-slate-600">
            View your charges and message usage for the selected WhatsApp number.
          </p>
        </header>

        <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex flex-wrap items-center gap-3">
              <label className="flex min-w-[220px] flex-col gap-1 text-xs font-semibold text-slate-500">
                WhatsApp number
                <select
                  value={selectedPhoneNumberId}
                  onChange={event => { setSelectedPhoneNumberId(event.target.value); setAllowanceData(null); setAllowanceError(""); }}
                  disabled={numbersLoading || whatsappNumbers.length === 0}
                  className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-normal text-slate-700"
                >
                  {whatsappNumbers.length === 0 ? <option value="">No WhatsApp numbers</option> : null}
                  {whatsappNumbers.map(number => <option key={number.id} value={number.id}>{number.label}</option>)}
                </select>
              </label>
              <label className="flex items-center gap-2">
                <span className="text-xs font-semibold text-slate-500">
                  From
                </span>
                <input
                  type="date"
                  aria-label="From"
                  value={filters.from}
                  onChange={handleFilterChange("from")}
                  className="w-[170px] rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                />
              </label>

              <label className="flex items-center gap-2">
                <span className="text-xs font-semibold text-slate-500">
                  To
                </span>
                <input
                  type="date"
                  aria-label="To"
                  value={filters.to}
                  onChange={handleFilterChange("to")}
                  className="w-[170px] rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                />
              </label>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={handleApplyRange}
                disabled={isDashboardLoading || isLoading}
                className="rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-70"
              >
                Apply Range
              </button>
              <button
                type="button"
                onClick={handleResetRange}
                disabled={isDashboardLoading || isLoading}
                className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-70"
              >
                Reset
              </button>
            </div>
          </div>

        </div>

        <p className="text-xs text-slate-600">
          Usage &amp; Charges data is available from 1 October 2026.
        </p>

        <div role="tablist" aria-label="Usage views" className="flex items-center gap-4 border-b border-slate-200">
          <TabButton
            id="usage-dashboard-tab"
            panelId="usage-dashboard-panel"
            active={activeTab === TAB_DASHBOARD}
            onClick={() => setActiveTab(TAB_DASHBOARD)}
          >
            Usage Dashboard
          </TabButton>
          <TabButton
            id="usage-report-tab"
            panelId="usage-report-panel"
            active={activeTab === TAB_REPORT}
            onClick={() => setActiveTab(TAB_REPORT)}
          >
            Detailed Report
          </TabButton>
        </div>

        {activeTab === TAB_DASHBOARD ? (
        <div id="usage-dashboard-panel" role="tabpanel" aria-labelledby="usage-dashboard-tab" className="space-y-6">
          <section aria-label="Usage summary" className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-base font-semibold text-slate-900">Usage summary</h2>
              {!isDashboardLoading && !dashboardError && selectedPhoneNumberId ? (
                <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                  {summary.awaitingBillingDetails > 0 ? "Updating" : "Up to date"}
                </span>
              ) : null}
            </div>
            <p className="mt-1 text-xs text-slate-500">
              Paid, free and updating counts cover delivered messages in the selected report period.
            </p>
            <div className="mt-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <div role="group" aria-label="Total charges" className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                <div className="text-xs font-semibold text-slate-500">Total charges</div>
                <div className="mt-2">
                  {isDashboardLoading ? "..." : dashboardError || !selectedPhoneNumberId ? "-" : (
                    <MoneyStack items={summary.totalCharges} emptyLabel="No charges" />
                  )}
                </div>
              </div>
              {summaryMetrics.map(metric => (
                <div key={metric.label} role="group" aria-label={metric.label} className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                  <div className="text-xs font-semibold text-slate-500">{metric.label}</div>
                  <div className="mt-2 text-2xl font-semibold text-slate-900">
                    {isDashboardLoading ? "..." : dashboardError || !selectedPhoneNumberId ? "-" : formatCompactNumber(metric.value)}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section aria-label="Service Allowance" className="rounded-lg border border-emerald-100 bg-emerald-50/60 p-4">
            <h3 className="text-sm font-semibold text-slate-900">Service Allowance</h3>
            <p className="mt-1 text-xs text-slate-600">
              For this number's current WABA-local month, independent of the selected report dates.
            </p>
            {allowanceLoading ? <p className="mt-3 text-sm text-slate-500">Loading allowance...</p> : allowanceData ? (
              <div className="mt-3 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
                <div><div className="text-xs text-slate-500">Used</div><div className="font-semibold text-slate-900">{allowanceField("consumedCount", "ConsumedCount")}</div></div>
                <div><div className="text-xs text-slate-500">Pending</div><div className="font-semibold text-slate-900">{allowanceField("reservedCount", "ReservedCount")}</div></div>
                <div><div className="text-xs text-slate-500">Remaining</div><div className="font-semibold text-slate-900">{allowanceField("remainingAllocatableCount", "RemainingAllocatableCount")}</div></div>
                <div><div className="text-xs text-slate-500">Limit</div><div className="font-semibold text-slate-900">{allowanceField("allowanceLimit", "AllowanceLimit")}</div></div>
                <div className="col-span-2 sm:col-span-4"><div className="text-xs text-slate-500">Current period</div><div className="font-medium text-slate-700">{formatDateTime(allowanceField("periodStartUtc", "PeriodStartUtc"))} to {formatDateTime(allowanceField("periodEndUtc", "PeriodEndUtc"))}</div></div>
              </div>
            ) : <p className="mt-3 text-sm text-slate-500">Allowance data is unavailable or not configured.</p>}
            {allowanceError ? <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">{allowanceError}</div> : null}
          </section>

          {dashboardError ? (
            <div role="alert" className="rounded-lg border border-rose-200 bg-rose-50 px-5 py-4 text-sm text-rose-700">
              {dashboardError}
            </div>
          ) : null}

              <div className="grid gap-6 xl:grid-cols-[minmax(0,2fr)_minmax(320px,1fr)]">
                <ChartShell
                  title="Usage Trend"
                  subtitle="Sent, paid, free and updating messages across the selected report period."
                  loading={isDashboardLoading}
                  emptyMessage={
                    volumeTrendData.length === 0
                      ? "No trend data available for this range."
                      : ""
                  }
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={volumeTrendData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                      <YAxis
                        allowDecimals={false}
                        tickFormatter={formatCompactNumber}
                      />
                      <Tooltip content={<MetricTooltip />} />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="sentMessages"
                        name="Sent Messages"
                        stroke="#0f766e"
                        strokeWidth={2.5}
                        dot={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="paidMessages"
                        name="Paid Messages"
                        stroke="#0ea5e9"
                        strokeWidth={2.5}
                        dot={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="freeMessages"
                        name="Free Messages"
                        stroke="#f59e0b"
                        strokeWidth={2.5}
                        dot={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="awaitingBillingDetails"
                        name="Updating"
                        stroke="#8b5cf6"
                        strokeWidth={2.5}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </ChartShell>

                <SectionCard
                  title="Insights"
                  subtitle="A quick read on category mix and charge direction."
                >
                  <div className="grid gap-4">
                    <InsightCard
                      title="Highest volume category"
                      helper="Based on delivered message volume."
                    >
                      <div className="text-lg font-semibold text-slate-950">
                        {dashboardData.insights.highestVolumeCategory
                          ? formatCategory(
                              dashboardData.insights.highestVolumeCategory,
                            )
                          : "Not available"}
                      </div>
                    </InsightCard>
                  </div>
                </SectionCard>
              </div>

              <div className="grid gap-6 xl:grid-cols-[minmax(0,2fr)_minmax(320px,1fr)]">
                <ChartShell
                  title="Charges Trend"
                  subtitle="Charge movement by currency across the selected range."
                  loading={isDashboardLoading}
                  emptyMessage={
                    chargeTrend.rows.length === 0 || chargeTrend.currencies.length === 0
                      ? "No charge trend data available for this range."
                      : ""
                  }
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chargeTrend.rows}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                      <YAxis tickFormatter={formatCompactNumber} />
                      <Tooltip
                        content={
                          <MetricTooltip
                            valueFormatter={formatChargeTooltipValue}
                          />
                        }
                      />
                      <Legend />
                      {chargeTrend.currencies.map((currency, index) => (
                        <Line
                          key={currency}
                          type="monotone"
                          dataKey={`charge_${currency}`}
                          name={`${currency} Charges`}
                          stroke={
                            CHARGE_SERIES_COLORS[
                              index % CHARGE_SERIES_COLORS.length
                            ]
                          }
                          strokeWidth={2.5}
                          dot={false}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </ChartShell>

                <SectionCard
                  title="About These Numbers"
                  subtitle="Charges and message status for your selected report period."
                >
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm leading-relaxed text-slate-600">
                    Usage follows the selected send dates. Charges are confirmed
                    customer amounts after recorded refunds, shown separately by
                    currency. Updating means billing details are still being processed.
                  </div>
                </SectionCard>
              </div>

              <SectionCard
                title="Usage by category"
                subtitle="Delivered, paid, free and updating messages by category."
              >
                {isDashboardLoading ? (
                  <div className="flex items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-10 text-sm text-slate-500">
                    <Loader2 className="mr-2 h-4 w-4 animate-spin text-emerald-600" />
                    Loading category breakdown...
                  </div>
                ) : dashboardData.categories.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-10 text-center text-sm text-slate-500">
                    No category data available for this range.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-200">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                            Category
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                            Delivered
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                            Paid
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                            Free
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                            Updating
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                            Charges
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 bg-white">
                        {dashboardData.categories.map(category => (
                          <tr
                            key={category.category}
                            className="transition hover:bg-slate-50"
                          >
                            <td className="px-4 py-3.5 text-sm font-medium text-slate-900">
                              {formatCategory(category.category)}
                            </td>
                            <td className="px-4 py-3.5 text-right text-sm text-slate-700">
                              {formatCompactNumber(
                                category.deliveredMessages,
                              )}
                            </td>
                            <td className="px-4 py-3.5 text-right text-sm text-slate-700">
                              {formatCompactNumber(
                                category.paidMessages,
                              )}
                            </td>
                            <td className="px-4 py-3.5 text-right text-sm text-slate-700">
                              {formatCompactNumber(
                                category.freeMessages,
                              )}
                            </td>
                            <td className="px-4 py-3.5 text-right text-sm text-slate-700">
                              {formatCompactNumber(category.awaitingBillingDetails)}
                            </td>
                            <td className="px-4 py-3.5 text-right">
                              <MoneyStack
                                items={category.charges}
                                emptyLabel="No charges"
                                align="right"
                                dense
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </SectionCard>
        </div>
        ) : (
        <div id="usage-report-panel" role="tabpanel" aria-labelledby="usage-report-tab" className="space-y-6">
              <SectionCard
                title="Detailed Usage filters"
                subtitle="View the latest 100 usage records for this number and report period."
                actions={
                  <>
                    <button
                      type="button"
                      onClick={handleLoadReport}
                      disabled={isLoading}
                      className="rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-70"
                    >
                      {isLoading ? "Loading..." : "Load Report"}
                    </button>
                    <button
                      type="button"
                      onClick={handleExportReport}
                      disabled={isLoading || filteredRows.length === 0}
                      className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-70"
                    >
                      Export visible rows
                    </button>
                    <button
                      type="button"
                      onClick={handleResetReport}
                      disabled={isLoading}
                      className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-70"
                    >
                      Reset Filters
                    </button>
                  </>
                }
              >
                <div className="grid gap-3 lg:grid-cols-[minmax(0,1.4fr)_repeat(2,minmax(0,1fr))]">
                  <label className="space-y-1.5">
                    <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                      Search
                    </span>
                    <input
                      type="search"
                      value={reportSearch}
                      onChange={event => setReportSearch(event.target.value)}
                      placeholder="Search category, status, country or amount..."
                      className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                    />
                  </label>

                  <label className="space-y-1.5">
                    <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                      Category
                    </span>
                    <select
                      aria-label="Category"
                      value={filters.category}
                      onChange={handleFilterChange("category")}
                      className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                    >
                      {CATEGORY_OPTIONS.map(option => (
                        <option key={option.value || "all"} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="space-y-1.5">
                    <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                      Status
                    </span>
                    <select
                      aria-label="Status"
                      value={filters.status}
                      onChange={handleFilterChange("status")}
                      className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                    >
                      {STATUS_OPTIONS.map(option => (
                        <option key={option.value || "all"} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>

                </div>

                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
                    {formatCompactNumber(filteredRows.length)} visible
                  </span>
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
                    {formatCompactNumber(rows.length)} loaded
                  </span>
                  {reportFilterChips.map(chip => (
                    <button
                      key={chip.key}
                      type="button"
                      onClick={() => handleClearReportChip(chip.key)}
                      className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600 transition hover:border-slate-300 hover:text-slate-900"
                    >
                      {chip.label} x
                    </button>
                  ))}
                </div>
              </SectionCard>

              <SectionCard
                title="Detailed Usage"
                subtitle="Your frozen message rate and actual customer charge are shown separately."
              >
                {loadError ? (
                  <div role="alert" className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                    {loadError}
                  </div>
                ) : null}

                {isLoading ? (
                  <div className="rounded-lg border border-slate-200 bg-slate-50/80 px-4 py-10">
                    <div className="flex items-center justify-center gap-3 text-sm text-slate-500">
                      <Loader2 className="h-4 w-4 animate-spin text-emerald-600" />
                      <span>Loading usage charges...</span>
                    </div>
                    <div className="mt-6 grid gap-3">
                      <div className="h-10 rounded-lg bg-white/80" />
                      <div className="h-10 rounded-lg bg-white/80" />
                      <div className="h-10 rounded-lg bg-white/80" />
                    </div>
                  </div>
                ) : filteredRows.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-12 text-center">
                    <div className="text-base font-semibold text-slate-900">
                      No usage charges found for this period
                    </div>
                    <div className="mt-2 text-sm text-slate-500">
                      Adjust the report filters or widen the date range to see
                      more billing rows.
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="mb-4 flex flex-wrap items-center gap-3 text-sm text-slate-500">
                      <span className="rounded-full bg-slate-100 px-3 py-1 font-medium text-slate-700">
                        {formatCompactNumber(filteredRows.length)} visible rows
                      </span>
                      {rows.length !== filteredRows.length ? (
                        <span>
                          Filtered from {formatCompactNumber(rows.length)} loaded
                          rows.
                        </span>
                      ) : (
                        <span>
                          Showing up to {formatCompactNumber(DEFAULT_TAKE)} recent
                          rows from the selected range.
                        </span>
                      )}
                    </div>

                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-slate-200">
                        <thead className="bg-slate-50">
                          <tr>
                            {["Date / Time", "Category", "Country", "Status", "Your Rate", "Charge", "Currency"].map(label => (
                              <th key={label} className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                                {label}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 bg-white">
                          {filteredRows.map((row, index) => (
                            <tr
                              key={`${row?.id || row?.occurredAtUtc || "row"}-${row?.currency || "unknown"}-${index}`}
                              className="transition hover:bg-slate-50/70"
                            >
                              <td className="whitespace-nowrap px-5 py-3.5 text-sm text-slate-700">{formatDateTime(row?.occurredAtUtc)}</td>
                              <td className="px-5 py-3.5 text-sm font-medium text-slate-900">{formatCategory(row?.category)}</td>
                              <td className="px-5 py-3.5 text-sm text-slate-700">{row?.countryCode || "-"}</td>
                              <td className="px-5 py-3.5 text-sm">
                                <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${getStatusBadgeClass(row?.status)}`}>
                                  {row?.status || "-"}
                                </span>
                              </td>
                              <td className="whitespace-nowrap px-5 py-3.5 text-sm font-semibold text-slate-900">{formatUsageAmount(row?.yourRate, row?.currency)}</td>
                              <td className="whitespace-nowrap px-5 py-3.5 text-sm font-semibold text-slate-900">{formatUsageAmount(row?.charge, row?.currency)}</td>
                              <td className="px-5 py-3.5 text-sm text-slate-700">{row?.currency || "-"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </SectionCard>
        </div>
        )}
        </div>
    </div>
  );
}
